/**
 * DeparturesBlock — quién se va hoy, a qué hora y qué queda detrás.
 *
 * La salida tardía se modela de verdad porque el portal del huésped podrá
 * pedirla o comprarla cuando la disponibilidad lo permita (§7.2, §9): aquí se
 * ven los dos desenlaces, concedida y denegada. La limpieza que genera cada
 * salida se enlaza con su bloque en vez de repetirla.
 */
import React from "react";
import { Clock, Broom } from "@phosphor-icons/react";
import { formatEUR } from "@/lib/pmsSelectors";
import { HOUSEKEEPING_LABEL, HOUSEKEEPING_TONE } from "@/data/todayV2Demo";
import {
  TodayBlock, TodayCard, RowList, Row, Meta, StatusPill, QuickAction, EmptyState,
} from "@/pages/admin/pms/v2/V2Primitives";

export default function DeparturesBlock({
  departures, housekeeping, showGuestNames, onGoToSection,
}) {
  return (
    <TodayBlock
      id="today-departures"
      title="Salidas"
      count={departures.length}
      testId="today-departures"
    >
      {departures.length === 0 ? (
        <EmptyState testId="today-departures-empty">Hoy no se va nadie.</EmptyState>
      ) : (
        <TodayCard padding="p-0">
          <RowList testId="today-departures-list">
            {departures.map((d) => (
              <DepartureRow
                key={d.reservation.reservation_id}
                departure={d}
                task={housekeeping.find((t) => t.unit_id === d.unit.villa_id && t.advanceable)}
                showGuestNames={showGuestNames}
                onGoToSection={onGoToSection}
              />
            ))}
          </RowList>
        </TodayCard>
      )}
    </TodayBlock>
  );
}

function DepartureRow({ departure, task, showGuestNames, onGoToSection }) {
  const r = departure.reservation;
  const late = departure.late_checkout;

  return (
    <Row testId={`departure-${r.reservation_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-11 shrink-0 text-sm text-stone-900 dark:text-stone-100 tabular-nums">
          {departure.checkout_time}
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-sm text-stone-900 dark:text-stone-100 truncate">
            {showGuestNames ? r.guest.name : `${r.guest.adults + r.guest.children} huéspedes`}
            <span className="text-stone-400"> · {departure.unit.villa_id}</span>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {late && (
              <StatusPill
                tone={late.status === "granted" ? "emerald" : "stone"}
                dot={false}
                title={late.reason}
                testId={`departure-late-${r.reservation_id}`}
              >
                <span className="inline-flex items-center gap-1">
                  <Clock size={10} weight="fill" />
                  {late.status === "granted"
                    ? `Salida tardía ${late.until}`
                    : "Salida tardía denegada"}
                </span>
              </StatusPill>
            )}
            {task && (
              <StatusPill tone={HOUSEKEEPING_TONE[task.status]} dot={false}>
                <span className="inline-flex items-center gap-1">
                  <Broom size={10} weight="fill" />
                  Limpieza {HOUSEKEEPING_LABEL[task.status].toLowerCase()}
                </span>
              </StatusPill>
            )}
            {task?.same_day && <StatusPill tone="red">Llega otro huésped hoy</StatusPill>}
          </div>

          <Meta
            items={[
              departure.balance_due_eur > 0
                ? `Saldo ${formatEUR(departure.balance_due_eur)}`
                : "Sin saldo",
              late?.reason,
              late?.status === "granted" && late.price_eur ? formatEUR(late.price_eur) : null,
            ]}
            className="mt-0.5"
          />
        </div>

        <div className="shrink-0 flex items-center gap-1.5">
          <QuickAction
            label="Abrir"
            to={`/pms/reservations/${r.reservation_id}`}
            testId={`departure-open-${r.reservation_id}`}
          />
          {task && (
            <QuickAction
              label="Limpieza"
              onClick={() => onGoToSection("housekeeping")}
              testId={`departure-cleaning-${r.reservation_id}`}
            />
          )}
        </div>
      </div>
    </Row>
  );
}
